<template>
    <div class="feedback-modal-wrap" @click="handleClickModalBackground" v-if="tempUserFeedbackQuestionData">
        <div
            class="modal"
            :style="{
                top: computedModalTop,
            }"
            @click.stop
        >
            <p class="title h5">{{ tempUserFeedbackQuestionData?.Title }}</p>
            <p class="desc caption--regular">
                {{ tempUserFeedbackQuestionData?.Description }}
            </p>
            <ul class="feedback-list">
                <!-- <li
                    class="feedback-item"
                    :class="{ active: choice.IsSelected }"
                    v-for="choice in tempUserFeedbackQuestionData?.MultipleChoices"
                    :key="choice.AnswerKey"
                    v-show="!choice?.IsForNonChoice"
                    @click="handleSelectChoice(choice)"
                > -->
                <!-- TODO: 추후 확인 -->
                <li
                    class="feedback-item"
                    :class="{ active: choice.IsSelected }"
                    v-for="choice in tempUserFeedbackQuestionData?.MultipleChoices"
                    :key="choice.AnswerKey"
                    v-show="choice?.AnswerValue"
                    @click="handleSelectChoice(choice)"
                >
                    <div class="icon-wrap">
                        <p class="icon" :class="{ active: choice.IsSelected }"></p>
                    </div>
                    <p class="feedback-item-title caption">
                        {{ choice.AnswerValue }}
                    </p>
                </li>
            </ul>
            <textarea
                name="etc-feedback"
                id="etc-feedback"
                :placeholder="t('trend_report_feedback_text_area_placeholder')"
                :value="tempUserFeedbackQuestionData.SubjectiveChoice?.SubjectiveAnswerValue"
                @input="handleInputTextarea"
                v-if="tempUserFeedbackQuestionData.SubjectiveChoice"
                ref="textareaRef"
                @focus="handleFocusTextarea"
                @blur="handleBlurTextarea"
            ></textarea>
            <div class="feedback-button-wrap">
                <button class="feedback-button h6" @click="handleCloseModal">{{ t('button_close') }}</button>
                <button
                    class="feedback-button h6"
                    @click="handleSubmitFeedback"
                    :disabled="computedIsSubmitButtonEnabled"
                >
                    {{ t('button_submit') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { TrendReportFeedbackChoicesInfoDto, TrendReportFeedbackQuestionDto } from '@common/openapi-client/openapi';
import { computed, onMounted, ref } from 'vue';
import { useKeyboardDetection } from '@common/common/composables/useKeyboard';
import { isAppleDevice, isMobile } from '@common/common/utils/utils';
import { hideKeyboard } from '@common/common/utils/utils';
import { useI18n } from 'vue-i18n';
import { useUserInteractionLog } from '../../composables/useUserInteractionLog';

const { t } = useI18n();
const { saveLog } = useUserInteractionLog(false); // 피드백 모달은 일반 사용자용

const props = defineProps<{
    userFeedbackQuestionData: TrendReportFeedbackQuestionDto | null;
}>();

const { lastScrollY, trackFocus } = useKeyboardDetection();

const textareaRef = ref<HTMLTextAreaElement | null>(null);

const computedIsSubmitButtonEnabled = computed(() => {
    if (!tempUserFeedbackQuestionData.value) {
        return true;
    }

    if (tempUserFeedbackQuestionData.value.MultipleChoices?.some(choice => choice.IsSelected)) {
        return false;
    } else if (
        tempUserFeedbackQuestionData.value.SubjectiveChoice &&
        tempUserFeedbackQuestionData.value.SubjectiveChoice.SubjectiveAnswerValue &&
        tempUserFeedbackQuestionData.value.SubjectiveChoice.SubjectiveAnswerValue.length > 0
    ) {
        return false;
    } else {
        return true;
    }
});

const tempUserFeedbackQuestionData = ref<TrendReportFeedbackQuestionDto | null>(null);

const computedModalTop = computed(() => {
    // iOS는 키보드 영역만큼 위로 올리지 않고 중앙 유지
    if (isAppleDevice()) return 'calc(50%)';
    return `calc(50% - ${keyboardHeight.value}px)`;
});

const emit = defineEmits(['closeModal', 'submitFeedback']);
const handleCloseModal = () => {
    saveLog(163, 'CLOSE_FEEDBACK_BUTTON_CLICK', '');
    emit('closeModal');
};

const handleClickModalBackground = () => {
    if (!isMobile()) return handleCloseModal();

    const scrollDiff = Math.abs(window.scrollY - lastScrollY.value);

    if (isAppleDevice() && scrollDiff > 10) {
        textareaRef.value?.blur();
        hideKeyboard();
        return;
    }

    // 안드로이드 인앱 웹뷰인 경우 높이를 정적으로 넣어놨기 때문에 visualViewport를 이용해 키보드 감지를 할 수 없음
    if (isFocusedTextarea.value) {
        hideKeyboard();
        handleBlurTextarea();
        return;
    }

    handleCloseModal();
};

const handleSubmitFeedback = () => {
    const rawData = tempUserFeedbackQuestionData.value;

    const simplifiedMultipleChoices = (rawData?.MultipleChoices || [])
        .filter(choice => choice.IsSelected)
        .map(choice => ({
            AnswerKey: choice.AnswerKey,
            IsSelected: choice.IsSelected,
            IsPositive: choice.IsPositive,
            AnswerValue: choice.AnswerValue,
        }));

    const simplifiedSubjectiveChoice = rawData?.SubjectiveChoice
        ? {
              AnswerKey: rawData.SubjectiveChoice.AnswerKey,
              IsSelected:
                  rawData.SubjectiveChoice?.SubjectiveAnswerValue !== null &&
                  (rawData.SubjectiveChoice?.SubjectiveAnswerValue ?? '').length > 0
                      ? true
                      : false,
              IsPositive: rawData.SubjectiveChoice.IsPositive,
              AnswerValue: rawData.SubjectiveChoice.AnswerValue,
              SubjectiveAnswerValue: rawData.SubjectiveChoice.SubjectiveAnswerValue,
          }
        : null;

    const feedbackData = {
        MultipleChoices: simplifiedMultipleChoices,
        SubjectiveChoice: simplifiedSubjectiveChoice,
    };

    saveLog(163, 'SUBMIT_FEEDBACK_BUTTON_CLICK', '');
    emit('submitFeedback', feedbackData);
};

const handleSelectChoice = (choice: TrendReportFeedbackChoicesInfoDto) => {
    choice.IsSelected = !choice.IsSelected;
};

const isFocusedTextarea = ref(false);
const keyboardHeight = ref(0);

const handleFocusTextarea = () => {
    isFocusedTextarea.value = true;

    const windowHeight = window.innerHeight;
    keyboardHeight.value = windowHeight / 3 + 10;
};

const handleBlurTextarea = () => {
    // 배경을 클릭하는 경우 isFocusedTextarea을 감지하기 위해 50ms 뒤에 실행
    setTimeout(() => {
        isFocusedTextarea.value = false;
        keyboardHeight.value = 0;
    }, 50);
};

const handleInputTextarea = (e: Event) => {
    const textarea = e.target as HTMLTextAreaElement;
    if (tempUserFeedbackQuestionData.value?.SubjectiveChoice) {
        tempUserFeedbackQuestionData.value.SubjectiveChoice.SubjectiveAnswerValue = textarea.value;
    }
};

onMounted(() => {
    saveLog(163, 'VIEW_FEEDBACK_MODAL', '');

    tempUserFeedbackQuestionData.value = props.userFeedbackQuestionData;

    if (tempUserFeedbackQuestionData.value?.SubjectiveChoice) {
        tempUserFeedbackQuestionData.value.SubjectiveChoice.SubjectiveAnswerValue =
            tempUserFeedbackQuestionData.value.SubjectiveChoice?.SubjectiveAnswerValue ?? '';
    }

    if (!textareaRef.value) return;
    trackFocus(textareaRef.value);
});
</script>

<style scoped lang="scss">
.feedback-modal-wrap {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;
    box-sizing: border-box;
    background-color: rgba(0, 0, 0, 0.5);

    .modal {
        width: calc(100% - 2.8rem);
        max-width: 36.2rem;
        min-height: 60%;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        background-color: $grey-1;
        border-radius: 2rem;
        padding: 3rem 1rem 1.5rem 1rem;
        box-sizing: border-box;
        transition: top 0.3s ease-in-out;

        .desc {
            white-space: pre-line;
            padding: 1rem 0 0 0;
        }
    }

    .feedback-list {
        width: 100%;
        display: flex;
        flex-direction: column;
        padding: 1.5rem 0;

        li {
            display: flex;
            align-items: flex-start;
            background-color: $grey-3;
            color: $grey-7;
            border-radius: 1rem;
            padding: 0.8rem 1rem;
            transition: all 0.3s ease-in-out;

            &.active {
                background-color: $grey-8;
                color: $primary-white;
            }

            .icon-wrap {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 1.25rem;
                height: 1.8rem;
                margin-right: 1rem;
            }

            .icon {
                width: 1.25rem;
                height: 0.75rem;
                background: url('../../../public/assets/images/ic_check.png') no-repeat center center / 100% 100%;

                &.active {
                    background: url('../../../public/assets/images/ic_check_blue.png') no-repeat center center / 100%
                        100%;
                }
            }

            & + li {
                margin-top: 1rem;
            }
        }
    }

    textarea {
        width: 100%;
        height: 7.6rem;
        box-sizing: border-box;
        border-radius: 1rem;
        border: 1px solid $grey-4;
        padding: 1rem;
        resize: none;
        font-size: 1.2rem;
        line-height: 150%;
        color: $grey-7;

        &::placeholder {
            color: $grey-5;
            font-size: 1.2rem;
            line-height: 150%;
        }
    }

    .feedback-button-wrap {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 1rem;

        .feedback-button {
            width: calc(50% - 0.75rem);
            box-sizing: border-box;
            height: 5rem;
            border-radius: 1.5rem;

            &:first-child {
                background-color: $primary-white;
                border: 1px solid $grey-4;
                color: $primary-black;
            }

            &:last-child {
                background-color: $grey-8;
                color: $pro-secondary-blue;

                &:disabled {
                    background-color: $grey-7;
                    color: $primary-white;
                }
            }
        }
    }
}

.feedback-item-title {
    text-align: left;
}
</style>
